/**
 * Symbolic differentiation.
 *
 * WCA needs the *sign* of each partial to pick a corner (PRD §4.5) and RSS
 * needs the magnitude. Both are far more accurate and much cheaper from a
 * symbolic derivative than from finite differences, and the symbolic form can
 * additionally be evaluated at many points to test monotonicity.
 *
 * Non-smooth nodes (`abs`, `min`, `max`, `if`, comparisons, rounding) have no
 * meaningful derivative at their switching points. Rather than emit a
 * subgradient and let it quietly become a wrong sensitivity, `differentiate`
 * throws NonDifferentiable and the caller falls back to central differences
 * with the result marked approximate.
 */

import {
  type Node,
  num,
  add,
  sub,
  mul,
  div,
  pow,
  neg,
  call,
} from './ast.ts';

export class NonDifferentiable extends Error {
  constructor(readonly what: string) {
    super(`Cannot symbolically differentiate ${what}`);
    this.name = 'NonDifferentiable';
  }
}

/** d(node)/d(varName), as a new AST. */
export function differentiate(node: Node, varName: string): Node {
  switch (node.kind) {
    case 'num':
      return num(0);

    case 'ref':
      return num(node.name === varName ? 1 : 0);

    case 'neg':
      return neg(differentiate(node.operand, varName));

    case 'binary': {
      const { left: u, right: v } = node;
      const du = differentiate(u, varName);

      switch (node.op) {
        case '+':
          return add(du, differentiate(v, varName));
        case '-':
          return sub(du, differentiate(v, varName));
        case '*': {
          // (uv)' = u'v + uv'
          const dv = differentiate(v, varName);
          return add(mul(du, v), mul(u, dv));
        }
        case '/': {
          // (u/v)' = (u'v - uv') / v^2
          const dv = differentiate(v, varName);
          return div(sub(mul(du, v), mul(u, dv)), pow(v, num(2)));
        }
        case '^': {
          const dv = differentiate(v, varName);
          const dvIsZero = dv.kind === 'num' && dv.value === 0;
          const duIsZero = du.kind === 'num' && du.value === 0;

          if (dvIsZero) {
            // Constant exponent: n*u^(n-1) * u'
            const exponentMinusOne =
              v.kind === 'num' ? num(v.value - 1) : sub(v, num(1));
            return mul(mul(v, pow(u, exponentMinusOne)), du);
          }
          if (duIsZero) {
            // Constant base: a^v * ln(a) * v'
            return mul(mul(pow(u, v), call('ln', [u])), dv);
          }
          // General case: u^v * (v' ln u + v u'/u)
          return mul(
            pow(u, v),
            add(mul(dv, call('ln', [u])), div(mul(v, du), u)),
          );
        }
      }
      break;
    }

    case 'call': {
      const [a0, a1] = node.args;
      const d0 = () => differentiate(a0, varName);

      switch (node.name) {
        case 'sqrt':
          // 1 / (2 sqrt(u)) * u'
          return div(d0(), mul(num(2), call('sqrt', [a0])));
        case 'exp':
          return mul(call('exp', [a0]), d0());
        case 'ln':
          return div(d0(), a0);
        case 'log10':
          return div(d0(), mul(a0, num(Math.LN10)));
        case 'sin':
          return mul(call('cos', [a0]), d0());
        case 'cos':
          return neg(mul(call('sin', [a0]), d0()));
        case 'tan':
          // sec^2(u) = 1 + tan^2(u)
          return mul(add(num(1), pow(call('tan', [a0]), num(2))), d0());
        case 'asin':
          return div(d0(), call('sqrt', [sub(num(1), pow(a0, num(2)))]));
        case 'acos':
          return neg(div(d0(), call('sqrt', [sub(num(1), pow(a0, num(2)))])));
        case 'atan':
          return div(d0(), add(num(1), pow(a0, num(2))));
        case 'atan2': {
          // d/dx atan2(y, x) with both arguments potentially varying:
          // (y' x - y x') / (x^2 + y^2)
          const dy = differentiate(a0, varName);
          const dx = differentiate(a1, varName);
          return div(
            sub(mul(dy, a1), mul(a0, dx)),
            add(pow(a1, num(2)), pow(a0, num(2))),
          );
        }
        case 'abs':
        case 'min':
        case 'max':
        case 'floor':
        case 'ceil':
        case 'round':
          throw new NonDifferentiable(`${node.name}()`);
      }
      break;
    }

    case 'if':
      throw new NonDifferentiable('if()');

    case 'compare':
      throw new NonDifferentiable('a comparison');
  }

  throw new NonDifferentiable((node as { kind: string }).kind);
}

/** True when `differentiate` will succeed for every variable in the expression. */
export function canDifferentiate(node: Node): boolean {
  try {
    differentiate(node, ' never-matches');
    return true;
  } catch {
    return false;
  }
}
